import {
  AuthSessionChangedError,
  captureAuthSession,
  isAuthSessionUnchanged,
  type AuthSessionOwner,
} from "lib/auth-session-ownership";

export type AuthAttempt = {
  session: AuthSessionOwner;
  applied: boolean;
  cancelled: boolean;
  kind: "login" | "reauthentication";
};

let currentAttempt: AuthAttempt | undefined;
const resultAttempts = new WeakMap<object, AuthAttempt>();

/** Anonymous requests also need an identity; their signed-session owners are identical. */
export function beginAuthAttempt(): AuthAttempt {
  const attempt: AuthAttempt = {
    session: captureAuthSession(),
    applied: false,
    cancelled: false,
    kind: "login",
  };
  currentAttempt = attempt;
  return attempt;
}

/** Reauthentication is local to its flow and does not supersede unrelated login/read work. */
export function beginReauthenticationAttempt(): AuthAttempt {
  return {
    session: captureAuthSession(),
    applied: false,
    cancelled: false,
    kind: "reauthentication",
  };
}

function isAttemptActive(attempt: AuthAttempt): boolean {
  return !attempt.cancelled && (attempt.kind === "reauthentication" || currentAttempt === attempt);
}

export function isAuthAttemptCurrent(attempt: AuthAttempt): boolean {
  return isAttemptActive(attempt) && isAuthSessionUnchanged(attempt.session);
}

export function assertAuthAttemptCurrent(attempt: AuthAttempt): void {
  if (!isAuthAttemptCurrent(attempt)) throw new AuthSessionChangedError();
}

/** Resetting an older mutation must not cancel a newer flow. */
export function cancelAuthAttempt(attempt: AuthAttempt | undefined): void {
  if (attempt) attempt.cancelled = true;
  if (currentAttempt === attempt) currentAttempt = undefined;
}

/** Applied login may navigate; reauthentication still needs its mounted continuation. */
export function cancelPendingAuthAttempt(attempt: AuthAttempt | undefined): void {
  if (attempt?.kind === "reauthentication" || !attempt?.applied) cancelAuthAttempt(attempt);
}

export function authAttemptFetchOptions(attempt: AuthAttempt) {
  return {
    onRequest: () => assertAuthAttemptCurrent(attempt),
    onSuccess: () => assertAuthAttemptCurrent(attempt),
  };
}

export function tagAuthAttemptResult<T>(result: T, attempt: AuthAttempt): T {
  if (result !== null && typeof result === "object") resultAttempts.set(result, attempt);
  return result;
}

export function getAuthAttemptForResult(result: object): AuthAttempt | undefined {
  return resultAttempts.get(result);
}

/** Bind only the expected session produced by this apply, never whatever is current later. */
export function bindAuthAttemptResult(result: object, expectedSession: AuthSessionOwner): void {
  const attempt = resultAttempts.get(result);
  if (!attempt) return;
  bindAuthAttempt(attempt, expectedSession);
}

export function bindAuthAttempt(attempt: AuthAttempt, expectedSession: AuthSessionOwner): void {
  if (!isAttemptActive(attempt) || !isAuthSessionUnchanged(expectedSession)) {
    throw new AuthSessionChangedError();
  }
  attempt.session = expectedSession;
  attempt.applied = true;
}
